import React, { useCallback } from 'react';
import { Tabs, Tab } from '@mui/material';
import { MoreHoriz as MoreHorizIcon } from '@mui/icons-material';
import { Box, IconButton } from '@weave-mui/material';
import { EditM as EditIcon } from '@weave-mui/icons-weave';

/**
 * Channels component displays a list of channels as tabs with optional actions
 * @param {Object} props - Component props
 * @param {Array} props.channels - Array of channel objects with subcategoryname, subcategoryid, and status
 * @param {Function} props.onClick - Callback function when a channel is clicked
 * @param {string} props.selectedChannel - ID of the currently selected channel
 * @param {string} [props.environment=''] - Current environment to check channel status
 * @param {Array} [props.actions=[]] - Array of action objects with name, text, and onClick
 * @param {Object} [props.activeChannelSecondaryAction=null] - Secondary action for the active channel with text and onClick
 */
const Channels = ({
  channels,
  onClick,
  selectedChannel,
  environment = '',
  actions = [],
  activeChannelSecondaryAction = null,
}) => {
  const handleTabChange = useCallback(
    (event, newValue) => {
      onClick(newValue);
    },
    [onClick]
  );

  // Find the index of the selected channel for Tabs value
  const selectedIndex = channels.findIndex(
    (channel) => channel.subcategoryid === selectedChannel
  );
  const tabValue = selectedIndex >= 0 ? selectedChannel : false;

  const actionsElement =
    actions.length === 0 ? null : (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          marginLeft: 'auto',
          position: 'relative',
          flexShrink: 0,
        }}
      >
        <IconButton
          id="test-case-secondary-actions"
          data-toggle="dropdown"
          size="small"
          sx={{
            padding: 0,
            border: '1px solid black',
            borderRadius: 1,
          }}
        >
          <MoreHorizIcon />
        </IconButton>

        <div
          className="dropdown-menu dropdown-menu-right"
          aria-labelledby="test-case-secondary-actions"
        >
          {actions.map((action) => (
            <button
              key={`action-${action.name}`}
              className="dropdown-item"
              onClick={action.onClick}
            >
              {action.text}
            </button>
          ))}
        </div>
      </Box>
    );

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        bgcolor: 'background.paper',
        borderBottom: 1,
        borderColor: 'divider',
        width: '100%',
        maxWidth: '100%',
        minWidth: 0,
      }}
    >
      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        variant="scrollable"
        scrollButtons="auto"
        sx={{
          flexGrow: 1,
          flexShrink: 1,
          minHeight: 48,
          minWidth: 0,
          maxWidth: '100%',
          width: 0, // Force flex item to respect container width
          '& .MuiTabs-indicator': {
            height: 2,
            backgroundColor: 'text.primary',
          },
          '& .MuiTabs-scrollButtons': {
            '&.Mui-disabled': {
              opacity: 0.3,
            },
          },
          '& .MuiTabs-scroller': {
            // Allow internal scrolling for the tabs
            overflowX: 'auto',
            overflowY: 'hidden',
          },
          '& .MuiTabs-flexContainer': {
            // Prevent flex container from forcing expansion
            overflow: 'visible',
          },
        }}
      >
        {channels.map((channel) => {
          const { subcategoryname, subcategoryid } = channel;
          const isDisabled =
            environment && channel.status[environment] === 'disabled';
          const isSelected = channel.subcategoryid === selectedChannel;

          return (
            <Tab
              key={`channel-${subcategoryid}`}
              label={
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  {isSelected && activeChannelSecondaryAction && (
                    <IconButton
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        activeChannelSecondaryAction.onClick();
                      }}
                      sx={{
                        padding: 0.5,
                        mr: 0.5,
                      }}
                    >
                      <EditIcon style={{ fontSize: 20 }} />
                    </IconButton>
                  )}
                  {subcategoryname}
                </Box>
              }
              value={subcategoryid}
              disabled={isDisabled}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                fontSize: '16px',
                lineHeight: '20px',
                minHeight: 48,
                px: 2,
                py: 1.5,
                color: 'text.secondary',
                '&.Mui-selected': {
                  color: 'text.primary',
                },
                '&.Mui-disabled': {
                  opacity: 0.38,
                },
              }}
            />
          );
        })}
      </Tabs>
      {actionsElement}
    </Box>
  );
};

export default React.memo(Channels);
